import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import { FootballerAuthService, SocialProvider, usesFootballerAuth } from '../shared/services/footballer-auth.service';

@Component({
  selector: 'app-footballer-auth', standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './footballer-auth.component.html',
  styleUrls: ['./footballer-auth.component.css']
})
export class FootballerAuthComponent implements OnDestroy {
  get googleEnabled(): boolean { return this.auth.googleEnabled; }
  readonly providers: SocialProvider[] = ['google', 'facebook', 'apple'];
  availableProviders: SocialProvider[] = [];
  linkAttempt: ReturnType<FootballerAuthService['consumeBrowserAttempt']> | null = null;
  private receipt = '';
  get linkConfirmKey(): string { return this.linkAttempt?.provider === 'google' ? 'footballerAuth.googleLinkConfirm'
    : this.linkAttempt?.provider === 'apple' ? 'footballerAuth.appleLinkConfirm' : 'footballerAuth.linkConfirm'; }
  mode: 'login' | 'register' | 'request' | 'password' = 'login';
  email = '';
  password = '';
  confirmation = '';
  busy = false;
  message = '';
  error = '';
  sentEmail = '';
  resendSeconds = 0;
  private resendDeadline = 0;
  private resendTimer: ReturnType<typeof setInterval> | undefined;
  ngOnDestroy(): void { clearInterval(this.resendTimer); }
  private startResendCooldown(): void {
    this.resendDeadline = Date.now() + 60000;
    this.resendSeconds = 60;
    clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendSeconds = Math.max(0, Math.ceil((this.resendDeadline - Date.now()) / 1000));
      if (!this.resendSeconds) clearInterval(this.resendTimer);
    }, 1000);
  }
  private proof = '';
  private target = '/';
  get canOpenPasswordInApp(): boolean {
    return this.mode === 'password' && !!this.proof && !this.busy
      && ['https://app.staging.footballer.zone', 'https://next.footballer.zone'].includes(window.location.origin);
  }

  get passwordAppLink(): string {
    // Explicit HTTPS navigation, never an interceptable custom scheme. Browsers
    // may retain the link on the web; neither opening path consumes the proof.
    return this.canOpenPasswordInApp ? `${window.location.origin}/auth#token=${this.proof}` : '';
  }

  constructor(private auth: FootballerAuthService, route: ActivatedRoute, private router: Router,
    private translate: TranslateService) {
    if (!usesFootballerAuth) { void router.navigateByUrl('/'); return; }
    this.target = auth.safeTarget(route.snapshot.queryParamMap.get('target'));
    const fragment = window.location.hash.replace(/^#/, '');
    const token = new URLSearchParams(fragment).get('token');
    const social = new URLSearchParams(fragment);
    if (social.has('state')) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
      void this.finishSocial(social);
    } else if (token !== null) {
      // Consume only after explicit form submission; remove proof from history before any async work.
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      this.mode = 'password';
      if (/^[A-Za-z0-9_-]{43}$/.test(token)) this.proof = token;
      else this.error = 'footballerAuth.invalidLink';
    } else if (['complete', 'failed'].includes(route.snapshot.queryParamMap.get('social'))) {
      this.target = auth.consumeSocialTarget();
      window.history.replaceState(window.history.state, '', window.location.pathname);
      if (route.snapshot.queryParamMap.get('social') === 'complete') void this.finishGoogle();
      else this.error = 'footballerAuth.failed';
    } else if (route.snapshot.queryParamMap.get('mode') === 'register') this.mode = 'register';
    else if (route.snapshot.queryParamMap.get('mode') === 'request') {
      this.mode = 'request';
      this.email = this.auth.user$.value?.email || '';
    }
    void this.auth.socialProviders().then(providers => this.availableProviders = providers).catch(() => {
      this.availableProviders = [];
      if (!this.error && (this.mode === 'login' || this.mode === 'register')) this.error = 'footballerAuth.methodsUnavailable';
    });
  }

  async socialLogin(provider: SocialProvider): Promise<void> {
    if (this.busy || !this.availableProviders.includes(provider)) return;
    this.busy = true; this.error = ''; this.password = '';
    try { await this.auth.beginSocial(provider, this.target, this.mode === 'register', this.translate.currentLang || 'en'); }
    catch (error) { this.showSocialError(error); this.busy = false; }
  }

  private async finishSocial(params: URLSearchParams): Promise<void> {
    this.busy = true;
    try {
      const attempt = this.auth.consumeBrowserAttempt(params.get('state') || '');
      this.target = attempt.target;
      if (params.has('error')) throw { error: { code: params.get('error') } };
      this.receipt = params.get('receipt') || '';
      await firstValueFrom(this.auth.isLoading$.pipe(filter(loading => !loading), take(1), timeout(20000)));
      if (attempt.subject) {
        if (this.auth.user$.value?.sub !== attempt.subject) throw new Error('social_account_changed');
        this.linkAttempt = attempt;
      } else {
        await this.auth.finishBrowserSocial(attempt, this.receipt);
        this.receipt = '';
        await this.finishGoogle();
      }
    } catch (error) { this.receipt = ''; this.showSocialError(error); }
    finally { this.busy = false; }
  }

  async confirmLink(): Promise<void> {
    if (!this.linkAttempt || this.busy) return;
    this.busy = true; this.error = '';
    try {
      await this.auth.finishBrowserSocial(this.linkAttempt, this.receipt);
      this.receipt = ''; this.linkAttempt = null;
      await this.router.navigateByUrl(this.target);
    } catch (error) { this.showSocialError(error); this.receipt = ''; this.linkAttempt = null; }
    finally { this.busy = false; }
  }

  cancelLink(): void { this.receipt = ''; this.linkAttempt = null; void this.router.navigateByUrl(this.target); }

  private showSocialError(error: any): void {
    const code = error?.error?.code;
    this.error = code === 'social_account_not_found' ? 'footballerAuth.socialNotLinked'
      : code === 'social_account_exists' ? 'footballerAuth.existingAccountLink'
      : code === 'reauthentication_required' ? 'footballerAuth.reauthenticate'
      : error?.status === 429 ? 'footballerAuth.rateLimited'
      : error?.status === 0 ? 'footballerAuth.connectionError' : 'footballerAuth.failed';
  }

  async googleLogin(): Promise<void> {
    if (this.busy) return;
    this.busy = true; this.error = ''; this.password = '';
    try { await this.auth.beginGoogle(this.target); }
    catch (_) { this.error = 'footballerAuth.failed'; this.busy = false; }
  }

  private async finishGoogle(): Promise<void> {
    this.busy = true;
    try {
      await firstValueFrom(this.auth.profileReady$.pipe(
        filter(subject => !!subject && subject === this.auth.user$.value?.sub), take(1), timeout(20000)));
      await this.router.navigateByUrl(this.target);
    } catch (_) { this.error = 'footballerAuth.failed'; }
    finally { this.busy = false; }
  }

  changeMode(mode: 'login' | 'register' | 'request'): void {
    if (this.busy) return;
    this.mode = mode;
    this.proof = '';
    this.password = '';
    this.confirmation = '';
    this.error = '';
    this.message = '';
    this.sentEmail = '';
  }

  editEmail(): void {
    if (this.mode === 'register' || this.mode === 'request') this.changeMode(this.mode);
  }

  async submit(): Promise<void> {
    if (this.busy || (this.sentEmail && Date.now() < this.resendDeadline)) return;
    this.error = '';
    if (this.mode === 'password' && (!this.proof || this.password !== this.confirmation
      || Array.from(this.password).length < 8 || Array.from(this.password).length > 128)) {
      this.error = this.proof ? 'footballerAuth.passwordRule' : 'footballerAuth.invalidLink';
      return;
    }
    this.busy = true;
    try {
      if (this.mode === 'login') {
        await firstValueFrom(this.auth.login(this.email, this.password));
        this.password = '';
        // Keep the form visible until the complete profile is ready for the destination screen.
        await firstValueFrom(this.auth.profileReady$.pipe(
          filter(subject => !!subject && subject === this.auth.user$.value?.sub), take(1), timeout(20000)));
        await this.router.navigateByUrl(this.target);
      } else if (this.mode === 'password') {
        await firstValueFrom(this.auth.setPassword(this.proof, this.password));
        this.proof = '';
        this.password = '';
        this.confirmation = '';
        this.mode = 'login';
        this.message = 'footballerAuth.passwordSaved';
      } else {
        const email = this.email.trim();
        await firstValueFrom(this.auth.requestEmail(email, this.translate.currentLang || 'en', this.mode === 'register'));
        this.sentEmail = email;
        this.startResendCooldown();
      }
    } catch (error) {
      const status = (error as any)?.status;
      this.error = status === 429 ? 'footballerAuth.rateLimited'
        : status === 0 ? 'footballerAuth.connectionError'
        : this.mode === 'password' && status === 401 ? 'footballerAuth.invalidLink'
        : 'footballerAuth.failed';
    } finally { this.busy = false; }
  }
}
